import type { ReactElement, ReactNode } from "react";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { EthernetPortIcon, Router01Icon, WirelessIcon } from "@hugeicons/core-free-icons";

import { Button } from "@/components/ui/button";
import type { HealthTone } from "@/lib/unifi/format";
import { cn } from "@/lib/utils";

// The UniFi hover cards use the composition of the share and QR cards:
// a titled state, 28 px label/value rows, quiet section labels, 32 px item
// rows and one quick action. Content is data, not sentences.

/** PopoverContent classes every UniFi card shares. */
export const HOVER_CARD = "gap-0 p-0 py-1.5 shadow-xl";

const DOT = {
  ok: "bg-success",
  partial: "bg-warning",
  down: "bg-destructive",
  none: "bg-muted-foreground/40",
} satisfies Record<HealthTone, string>;

export function HoverTitle({
  tone,
  icon,
  children,
  aside,
}: {
  tone?: HealthTone;
  icon?: ReactNode;
  children: ReactNode;
  /** Right-aligned figure, e.g. "6/7". */
  aside?: ReactNode;
}) {
  return (
    <p className="m-0 flex items-center gap-2 px-3 pt-1 pb-1.5 text-compact font-medium">
      {icon}
      {tone && (
        <span className={cn("size-2 shrink-0 rounded-full", DOT[tone])} aria-hidden="true" />
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {aside && (
        <span className="shrink-0 text-xs font-normal text-muted-foreground tabular-nums">
          {aside}
        </span>
      )}
    </p>
  );
}

export function HoverRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-7 items-center gap-3 px-3">
      <span className="w-24 shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1 truncate text-compact tabular-nums">{children}</span>
    </div>
  );
}

export function HoverSection({ label, count }: { label: string; count?: number }) {
  return (
    <p className="m-0 flex items-center gap-1.5 px-3 pt-2.5 pb-1 text-caption font-medium text-muted-foreground">
      {label}
      {count !== undefined && <span className="tabular-nums opacity-70">{count}</span>}
    </p>
  );
}

export function HoverDivider() {
  return <div className="my-1.5 border-t" />;
}

/** A 32 px item row: tile, name (+ quiet detail), trailing figure. */
export function HoverItem({
  tile,
  name,
  detail,
  trailing,
}: {
  tile: ReactNode;
  name: ReactNode;
  detail?: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <li className="flex h-8 items-center gap-2.5 px-3">
      {tile}
      <span className="min-w-0 flex-1 truncate text-compact">
        {name}
        {detail && <span className="text-muted-foreground"> · {detail}</span>}
      </span>
      {trailing && <span className="shrink-0 text-xs tabular-nums">{trailing}</span>}
    </li>
  );
}

export function HoverList({ children }: { children: ReactNode }) {
  return <ul className="m-0 list-none p-0">{children}</ul>;
}

export function HoverMore({ count }: { count: number }) {
  if (count <= 0) return null;

  return <p className="m-0 px-3 pb-1 text-xs text-muted-foreground">+{count}</p>;
}

/** Small tinted square holding an icon or a letter. */
export function HoverTile({
  icon,
  letter,
  tone = "muted",
}: {
  icon?: IconSvgElement;
  letter?: string;
  tone?: "muted" | "down" | "warn" | "info";
}) {
  return (
    <span
      className={cn(
        "flex size-5 shrink-0 items-center justify-center rounded-[5px] text-caption font-medium",
        tone === "muted" && "bg-secondary text-secondary-foreground",
        tone === "down" && "bg-destructive/10 text-destructive",
        tone === "warn" && "bg-warning/20 text-expiring-foreground",
        tone === "info" && "bg-info/15 text-info",
      )}
      aria-hidden="true"
    >
      {icon ? <HugeiconsIcon icon={icon} className="size-3" /> : letter}
    </span>
  );
}

export function deviceIcon(features: {
  isAccessPoint?: boolean;
  features?: string[];
  model?: string;
}) {
  const list = features.features ?? [];

  if (features.isAccessPoint || list.includes("accessPoint")) return WirelessIcon;

  if (list.includes("gateway")) return Router01Icon;

  return EthernetPortIcon;
}

/** The card's one quick action, as the share cards place it. Pass `render` for a link. */
export function HoverAction({
  icon,
  children,
  onClick,
  render,
}: {
  icon?: IconSvgElement;
  children: ReactNode;
  onClick?: () => void;
  render?: ReactElement;
}) {
  return (
    <div className="mt-1.5 border-t px-1.5 pt-1.5">
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-start text-muted-foreground"
        onClick={onClick}
        {...(render ? { nativeButton: false, render } : {})}
      >
        {icon && <HugeiconsIcon icon={icon} aria-hidden="true" />}
        {children}
      </Button>
    </div>
  );
}
